import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ChevronRight,
  Shield,
  Award,
  PoundSterling,
  DoorOpen,
  Ruler,
  PanelTop,
  Sparkles,
  CheckCircle,
  Phone,
  Star,
  Quote,
} from "lucide-react";

const testimonials = [
  {
    name: "Sarah M.",
    location: "Stockport",
    text: "Had 4 internal doors hung. Turned up on time, worked quickly and tidied up after. Doors fit perfectly - no sticking or gaps. Would definitely use again.",
    service: "Door Fitting",
    rating: 5
  },
  {
    name: "Dave T.",
    location: "Cheadle",
    text: "New skirting and architraves throughout the hallway. Really neat mitres, filled and ready for painting. Good communication throughout.",
    service: "Trimwork",
    rating: 5
  },
  {
    name: "Karen B.",
    location: "Bramhall",
    text: "Fitted shaker panelling in our living room. Took time to get it level and the finish is spot on. Fair price and no mess left behind.",
    service: "Wall Panelling",
    rating: 5
  }
];

const HomePage = () => {
  return (
    <div>
      <Helmet>
        <title>Expert Carpenter | Door Hanging & Fitting | Greater Manchester</title>
        <meta name="description" content="Professional door hanging, fitting and trimwork in Greater Manchester. Internal doors hung properly, skirting boards, architraves. 12-month guarantee, no VAT. Call 07300 405045." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
            <div className="text-center lg:text-center">
              {/* Logo above heading */}
              <img 
                src="https://customer-assets.emergentagent.com/job_timber-masters-12/artifacts/bzwdpjce_Expert-Carpenter%20logo.png" 
                alt="Expert Carpenter" 
                className="w-40 sm:w-56 md:w-64 lg:w-72 max-w-full h-auto mx-auto mb-8 sm:mb-10"
              />
              
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-2">
                Door Hanging &<br />
                <span className="text-[#C9A227]">Carpentry Done Right</span>
              </h1>
              
              <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed max-w-xl mx-auto">
                Internal doors that fit and close properly. Skirting and architraves fitted neatly. 
                Good preparation, tidy work, no fuss.
              </p>
              
              {/* Urgency Badge */}
              <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 bg-[#C9A227]/20 border border-[#C9A227]/40 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9A227] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-[#C9A227]"></span>
                </span>
                <span className="text-white text-xs sm:text-sm font-medium">Usually quoted within 24 hours</span>
              </div>

              {/* Trust Badges */}
              <div className="mt-4 sm:mt-5 flex flex-wrap justify-center gap-2">
                <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full">
                  <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#C9A227]" />
                  <span className="text-white text-xs sm:text-sm font-medium">12-Month Guarantee</span>
                </div>
                <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full">
                  <Award className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#C9A227]" />
                  <span className="text-white text-xs sm:text-sm font-medium">Price Promise</span>
                </div>
                <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full">
                  <PoundSterling className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#C9A227]" />
                  <span className="text-white text-xs sm:text-sm font-medium">No VAT</span>
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold text-sm sm:text-base h-11 sm:h-12 px-5 sm:px-6 w-full sm:w-auto"
                    data-testid="hero-quote-btn"
                  >
                    Get a Free Quote
                    <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                </Link>
                <a href="tel:07300405045" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 font-bold text-sm sm:text-base h-11 sm:h-12 w-full sm:w-auto"
                  >
                    <Phone className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    07300 405045
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative hidden lg:block">
              <div className="relative">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10">
                  <img
                    src="https://customer-assets.emergentagent.com/job_panache-review/artifacts/w9aafbvk_carpenter%20door.jpg"
                    alt="Carpenter fitting an internal door"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-[#2D5A4A] text-white p-5 rounded-xl shadow-xl">
                  <p className="text-sm opacity-80">Covering</p>
                  <p className="font-bold text-lg">Greater Manchester</p>
                  <p className="text-sm opacity-80">& Surrounding Areas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-10 sm:py-14 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-16">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-xs sm:text-sm">What We Do</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#3A3A3A]">
              Our Services
            </h2>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Reliable carpentry for your home. Doors hung properly, trimwork fitted neatly, jobs finished to a good standard.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {/* Door Fitting Card - PRIMARY */}
            <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white ring-2 ring-[#C9A227]">
              <div className="h-40 sm:h-48 overflow-hidden relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_panache-review/artifacts/w9aafbvk_carpenter%20door.jpg"
                  alt="Door hanging and fitting"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-[#C9A227] rounded-lg">
                      <DoorOpen className="h-4 w-4 text-black" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Door Fitting</h3>
                  </div>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-gray-600 mb-3 text-sm">
                  Internal doors hung and fitted properly. Doors that open, close and latch as they should.
                </p>
                <Link to="/doors">
                  <Button className="w-full h-10 text-sm bg-[#2D5A4A] hover:bg-[#234839] group-hover:bg-[#C9A227] group-hover:text-black transition-colors">
                    Learn More
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Trimwork Card */}
            <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white">
              <div className="h-40 sm:h-48 overflow-hidden relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_3648a13c-ee29-4e40-9949-2c424ba9ffea/artifacts/bnf93lib_image.png"
                  alt="Skirting boards and architraves"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-[#C9A227] rounded-lg">
                      <Ruler className="h-4 w-4 text-black" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Trimwork</h3>
                  </div>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-gray-600 mb-3 text-sm">
                  Skirting boards and architraves fitted with neat mitres and tight joints.
                </p>
                <Link to="/trimwork">
                  <Button className="w-full h-10 text-sm bg-[#2D5A4A] hover:bg-[#234839] group-hover:bg-[#C9A227] group-hover:text-black transition-colors">
                    Learn More
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Wall Panelling Card */}
            <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white">
              <div className="h-40 sm:h-48 overflow-hidden relative">
                <img
                  src="https://static.wixstatic.com/media/9778a6_8299edfc732243ddb7e786d66f3693e5~mv2.jpg/v1/fill/w_600,h_400,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/half%20wall%20paneling.jpg"
                  alt="Wall panelling"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-[#C9A227] rounded-lg">
                      <PanelTop className="h-4 w-4 text-black" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Panelling</h3>
                  </div>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-gray-600 mb-3 text-sm">
                  Wall panelling fitted and finished. Straight lines, level work, ready for painting.
                </p>
                <Link to="/panelling">
                  <Button className="w-full h-10 text-sm bg-[#2D5A4A] hover:bg-[#234839] group-hover:bg-[#C9A227] group-hover:text-black transition-colors">
                    Learn More
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Floor Sanding Card */}
            <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white">
              <div className="h-40 sm:h-48 overflow-hidden relative">
                <img
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop"
                  alt="Dust-free floor sanding"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-[#C9A227] rounded-lg">
                      <Sparkles className="h-4 w-4 text-black" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Floor Sanding</h3>
                  </div>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-gray-600 mb-3 text-sm">
                  Dust-free floor sanding with portable extraction. Your home stays spotless.
                </p>
                <Link to="/sanding">
                  <Button className="w-full h-10 text-sm bg-[#2D5A4A] hover:bg-[#234839] group-hover:bg-[#C9A227] group-hover:text-black transition-colors">
                    Learn More
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 sm:py-14 md:py-28 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-16">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-xs sm:text-sm">Customer Reviews</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#3A3A3A]">
              What Customers Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {testimonials.map((review, index) => (
              <Card key={index} className="p-5 sm:p-6 bg-white border-0 shadow-lg relative">
                <Quote className="absolute top-4 right-4 h-8 w-8 text-[#2D5A4A]/10" />
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 fill-[#C9A227] text-[#C9A227]" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 text-sm sm:text-base leading-relaxed">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[#3A3A3A]">{review.name}</p>
                    <p className="text-sm text-gray-500">{review.location}</p>
                  </div>
                  <span className="text-xs bg-[#2D5A4A]/10 text-[#2D5A4A] px-2 py-1 rounded-full font-medium">
                    {review.service}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-10 sm:py-14 md:py-28 bg-[#2D5A4A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-16">
            <p className="text-[#C9A227] font-semibold tracking-wider uppercase mb-2 text-xs sm:text-sm">Why Choose Us</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              What You Get
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 md:gap-8">
            <div className="text-center p-5 sm:p-6 md:p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 mx-auto mb-3 sm:mb-4 md:mb-6 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                <Shield className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-[#C9A227]" />
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">
                12-Month Guarantee
              </h3>
              <p className="text-white/70 text-sm sm:text-base">
                All work guaranteed for 12 months. If there's a problem, I'll come back and sort it.
              </p>
            </div>
            
            <div className="text-center p-5 sm:p-6 md:p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 mx-auto mb-3 sm:mb-4 md:mb-6 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                <Award className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-[#C9A227]" />
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">
                Fair Prices
              </h3>
              <p className="text-white/70 text-sm sm:text-base">
                Competitive rates. I'll beat any like-for-like written quote by 10%.
              </p>
            </div>
            
            <div className="text-center p-5 sm:p-6 md:p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 mx-auto mb-3 sm:mb-4 md:mb-6 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                <PoundSterling className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-[#C9A227]" />
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">No VAT</h3>
              <p className="text-white/70 text-sm sm:text-base">
                The price I quote is the price you pay. No hidden extras.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Platforms Banner */}
      <section className="py-8 sm:py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 text-sm mb-4">Trusted on these platforms</p>
          <img 
            src="https://customer-assets.emergentagent.com/job_woodwork-master-2/artifacts/tuiemu67_Trades%20Logo.png" 
            alt="Trusted trade platforms including Checkatrade, MyBuilder, Rated People, TaskRabbit" 
            className="w-full max-w-4xl mx-auto h-auto"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 sm:py-14 md:py-28 bg-[#f8f7f5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-5 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#3A3A3A] mb-4 sm:mb-5">
            Need a Carpenter?
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Get in touch for a free quote. I'll get back to you quickly to discuss your job.
          </p>
          <div className="flex flex-col gap-3 sm:gap-4">
            <Link to="/contact" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" className="bg-[#2D5A4A] hover:bg-[#234839] h-12 sm:h-14 px-8 sm:px-10 text-base sm:text-lg font-bold w-full" data-testid="home-get-quote-btn">
                Get a Free Quote
                <ChevronRight className="ml-2 h-5 w-5 sm:h-6 sm:w-6" />
              </Button>
            </Link>
            <a href="tel:07300405045" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" variant="outline" className="border-[#2D5A4A] text-[#2D5A4A] hover:bg-[#2D5A4A] hover:text-white h-12 sm:h-14 px-8 sm:px-10 text-base sm:text-lg font-bold w-full">
                <Phone className="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
                07300 405045
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
